import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { CONTACT_ROLE_LABEL } from "../../lib/accountTypes";
import { STAGE_LABELS } from "../../lib/crmHealth";
import VoiceCapture from "../../components/VoiceCapture";
import {
    Sparkle, X, CircleNotch, CheckSquare, Square, CalendarCheck, ListChecks, UserPlus,
    ArrowRight, WarningCircle, ArrowCounterClockwise, Smiley, SmileyMeh, SmileySad, SmileyNervous,
} from "@phosphor-icons/react";

const SENTIMENT = {
    positive: { label: "Positive", icon: Smiley, color: "#00853F", bg: "rgba(0,133,63,0.1)" },
    neutral: { label: "Neutral", icon: SmileyMeh, color: "#475569", bg: "rgba(71,85,105,0.1)" },
    cautious: { label: "Cautious", icon: SmileyNervous, color: "#d97706", bg: "rgba(217,119,6,0.12)" },
    negative: { label: "Negative", icon: SmileySad, color: "#dc2626", bg: "rgba(220,38,38,0.1)" },
};
const VISIT_TYPES = [["visit", "Site visit"], ["meeting", "Meeting"], ["call", "Phone call"]];

export function DebriefButton({ client, onDone }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button className="btn btn-primary" onClick={() => setOpen(true)} data-testid="debrief-open-btn">
                <Sparkle size={14} weight="fill" /> Visit debrief
            </button>
            {open && <VisitDebriefModal client={client} onClose={(saved) => { setOpen(false); if (saved) onDone?.(); }} />}
        </>
    );
}

export default function VisitDebriefModal({ client, onClose }) {
    const today = new Date().toISOString().slice(0, 10);
    const [step, setStep] = useState("capture"); // capture | analysing | review | saving | done
    const [notes, setNotes] = useState("");
    const [visitType, setVisitType] = useState("visit");
    const [visitDate, setVisitDate] = useState(today);
    const [err, setErr] = useState("");
    const [deb, setDeb] = useState(null);
    const [team, setTeam] = useState([]);
    const [summary, setSummary] = useState("");
    const [actions, setActions] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [stageOn, setStageOn] = useState(false);
    const [saved, setSaved] = useState(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && step !== "analysing" && step !== "saving") onClose(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [step, onClose]);

    async function analyse() {
        setErr(""); setStep("analysing");
        try {
            const { data } = await endpoints.createDebrief(client.id, { transcript: notes, visit_type: visitType, visit_date: visitDate });
            setDeb(data);
            setTeam(data.team || []);
            const r = data.result;
            setSummary(r.summary);
            setActions(r.suggested_actions.map((a) => ({ ...a, on: true })));
            setContacts(r.new_contacts.map((p) => ({ ...p, on: true })));
            setStageOn(!!r.client_position.suggested_stage && r.client_position.suggested_stage !== client.stage);
            setStep("review");
        } catch (e) { setErr(formatApiError(e)); setStep("capture"); }
    }

    async function save() {
        setErr(""); setStep("saving");
        const r = deb.result;
        try {
            const { data } = await endpoints.commitDebrief(client.id, deb.id, {
                summary,
                topics: r.topics,
                next_steps: r.next_steps,
                actions: actions.filter((a) => a.on).map(({ on, ...a }) => a),
                contacts: contacts.filter((p) => p.on).map(({ on, ...p }) => p),
                stage: stageOn ? r.client_position.suggested_stage : null,
            });
            setSaved(data);
            setStep("done");
        } catch (e) { setErr(formatApiError(e)); setStep("review"); }
    }

    const setAction = (i, patch) => setActions((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const r = deb?.result;
    const sent = r ? (SENTIMENT[r.client_position.sentiment] || SENTIMENT.neutral) : null;
    const SentIcon = sent?.icon;
    const ticked = actions.filter((a) => a.on).length + contacts.filter((p) => p.on).length + (stageOn ? 1 : 0);

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="debrief-modal">
            <div className="rom-card w-full max-w-2xl max-h-[94vh] flex flex-col">
                <div className="rom-card-head shrink-0">
                    <h3 className="flex items-center gap-1.5"><Sparkle size={14} weight="fill" className="text-[var(--rom-orange)]" /> Visit debrief · {client.name}</h3>
                    {step !== "analysing" && step !== "saving" && <button onClick={() => onClose(step === "done")}><X size={18} /></button>}
                </div>

                <div className="rom-card-body overflow-y-auto space-y-4">
                    {err && <div className="text-sm text-red-600 flex items-center gap-1.5" data-testid="debrief-error"><WarningCircle size={14} weight="bold" /> {err}</div>}

                    {(step === "capture" || step === "analysing") && (
                        <>
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="inline-flex rounded-[6px] border border-[var(--rom-border-strong)] overflow-hidden text-xs font-semibold">
                                    {VISIT_TYPES.map(([v, l]) => (
                                        <button key={v} onClick={() => setVisitType(v)} disabled={step === "analysing"} data-testid={`debrief-type-${v}`}
                                            className={`px-3 py-1.5 ${visitType === v ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>{l}</button>
                                    ))}
                                </div>
                                <input type="date" className="input h-8 w-auto" value={visitDate} max={today} onChange={(e) => setVisitDate(e.target.value)} disabled={step === "analysing"} data-testid="debrief-date" />
                            </div>
                            <div className="p-4 rounded-[8px] border border-dashed border-[var(--rom-border-strong)] bg-slate-50/60 space-y-3">
                                <VoiceCapture label="Talk through the visit" maxSeconds={300} disabled={step === "analysing"}
                                    onTranscribed={(t) => setNotes((n) => (n.trim() ? `${n.trim()}\n${t}` : t))} />
                                <p className="text-[11px] text-[var(--rom-text-3)]">Who you met, what you discussed, how they feel about us, what you promised, and when you'll follow up. You can record more than once, or type below.</p>
                            </div>
                            <textarea rows={7} className="input resize-y" placeholder="Or type / paste your notes here…" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={step === "analysing"} data-testid="debrief-notes" />
                            <div className="flex justify-end gap-2">
                                <button className="btn btn-ghost" onClick={() => onClose(false)} disabled={step === "analysing"}>Cancel</button>
                                <button className="btn btn-primary" onClick={analyse} disabled={step === "analysing" || notes.trim().length < 15} data-testid="debrief-analyse-btn">
                                    {step === "analysing" ? <><CircleNotch size={14} className="animate-spin" /> Reading your notes…</> : <><Sparkle size={14} weight="fill" /> Build debrief</>}
                                </button>
                            </div>
                        </>
                    )}

                    {(step === "review" || step === "saving") && r && (
                        <>
                            <Section title="Summary">
                                <textarea rows={3} className="input resize-y text-sm" value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="debrief-summary" />
                                {r.topics.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">{r.topics.map((t) => <span key={t} className="chip chip-gray text-[11px]">{t}</span>)}</div>
                                )}
                            </Section>

                            <Section title="Where they stand">
                                <div className="flex items-start gap-2">
                                    <span className="text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 shrink-0" style={{ color: sent.color, background: sent.bg }} data-testid="debrief-sentiment">
                                        <SentIcon size={13} weight="bold" /> {sent.label}
                                    </span>
                                    <div className="text-sm text-[var(--rom-text-2)]">{r.client_position.summary}</div>
                                </div>
                                {r.client_position.risks.length > 0 && (
                                    <ul className="mt-2 text-xs text-[var(--rom-text-2)] list-disc pl-5 space-y-0.5">{r.client_position.risks.map((x) => <li key={x}>{x}</li>)}</ul>
                                )}
                                {r.client_position.suggested_stage && r.client_position.suggested_stage !== client.stage && (
                                    <Tick on={stageOn} onToggle={() => setStageOn((v) => !v)} testId="debrief-stage-tick">
                                        <span className="text-sm">Move stage <b>{STAGE_LABELS[client.stage] || client.stage}</b> <ArrowRight size={11} className="inline" /> <b>{STAGE_LABELS[r.client_position.suggested_stage]}</b></span>
                                        {r.client_position.stage_reason && <div className="text-[11px] text-[var(--rom-text-3)]">{r.client_position.stage_reason}</div>}
                                    </Tick>
                                )}
                            </Section>

                            {r.next_steps.length > 0 && (
                                <Section title="Next steps">
                                    <ul className="text-sm list-disc pl-5 space-y-0.5">{r.next_steps.map((x) => <li key={x}>{x}</li>)}</ul>
                                </Section>
                            )}

                            <Section title={`Suggested follow-ups (${actions.filter((a) => a.on).length} of ${actions.length} ticked)`}>
                                {actions.length === 0 && <div className="text-sm text-[var(--rom-text-3)]">No follow-ups came out of these notes.</div>}
                                <div className="space-y-2">
                                    {actions.map((a, i) => (
                                        <div key={i} className={`p-2.5 border rounded-[6px] ${a.on ? "border-[var(--rom-navy)] bg-white" : "border-[var(--rom-border)] bg-slate-50 opacity-60"}`} data-testid={`debrief-action-${i}`}>
                                            <div className="flex items-start gap-2">
                                                <button onClick={() => setAction(i, { on: !a.on })} className="mt-1 shrink-0" data-testid={`debrief-action-tick-${i}`}>
                                                    {a.on ? <CheckSquare size={18} weight="fill" className="text-[var(--rom-navy)]" /> : <Square size={18} className="text-[var(--rom-text-3)]" />}
                                                </button>
                                                <div className="flex-1 min-w-0 space-y-1.5">
                                                    <input className="input h-8 text-sm font-semibold" value={a.title} onChange={(e) => setAction(i, { title: e.target.value })} disabled={!a.on} />
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <div className="inline-flex rounded-[4px] border border-[var(--rom-border-strong)] overflow-hidden">
                                                            <button onClick={() => setAction(i, { kind: "task" })} disabled={!a.on} title="Task"
                                                                className={`px-2 h-7 flex items-center gap-1 text-[11px] font-semibold ${a.kind === "task" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}><ListChecks size={12} /> Task</button>
                                                            <button onClick={() => setAction(i, { kind: "appointment" })} disabled={!a.on} title="Booking"
                                                                className={`px-2 h-7 flex items-center gap-1 text-[11px] font-semibold ${a.kind === "appointment" ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}><CalendarCheck size={12} /> Booking</button>
                                                        </div>
                                                        <input type="date" className="input h-7 w-auto text-xs" value={a.due_date} onChange={(e) => setAction(i, { due_date: e.target.value, date_inferred: false })} disabled={!a.on} />
                                                        {a.kind === "appointment" && <input type="time" className="input h-7 w-auto text-xs" value={a.time} onChange={(e) => setAction(i, { time: e.target.value })} disabled={!a.on} />}
                                                        <select className="input h-7 w-auto text-xs" value={a.assignee} onChange={(e) => setAction(i, { assignee: e.target.value })} disabled={!a.on}>
                                                            {!team.some((t) => t.name === a.assignee) && a.assignee && <option value={a.assignee}>{a.assignee}</option>}
                                                            {team.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                        </select>
                                                        {a.date_inferred && <span className="text-[10px] font-semibold text-[#d97706]" title="No date was mentioned — this is a suggestion">date suggested</span>}
                                                    </div>
                                                    {(a.detail || a.reason) && <div className="text-[11px] text-[var(--rom-text-3)]">{a.detail}{a.detail && a.reason ? " · " : ""}{a.reason && <i>“{a.reason}”</i>}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[11px] text-[var(--rom-text-3)] mt-1">Bookings go into the account's follow-ups (in-app calendar).</p>
                            </Section>

                            {contacts.length > 0 && (
                                <Section title="New people mentioned">
                                    <div className="space-y-1.5">
                                        {contacts.map((p, i) => (
                                            <Tick key={i} on={p.on} onToggle={() => setContacts((xs) => xs.map((x, j) => (j === i ? { ...x, on: !x.on } : x)))} testId={`debrief-contact-tick-${i}`}>
                                                <span className="text-sm flex items-center gap-1.5"><UserPlus size={13} weight="bold" /> <b>{p.name}</b>{p.title && ` · ${p.title}`}{p.role && <span className="chip chip-gray text-[10px]">{CONTACT_ROLE_LABEL[p.role] || p.role}</span>}</span>
                                                {(p.email || p.phone) && <div className="text-[11px] text-[var(--rom-text-3)]">{[p.email, p.phone].filter(Boolean).join(" · ")}</div>}
                                            </Tick>
                                        ))}
                                    </div>
                                </Section>
                            )}

                            <details className="text-xs text-[var(--rom-text-3)]">
                                <summary className="cursor-pointer">Your original notes</summary>
                                <div className="mt-1 whitespace-pre-wrap">{deb.transcript}</div>
                            </details>

                            <div className="flex items-center justify-between gap-2 pt-1">
                                <button className="btn btn-ghost" onClick={() => { setStep("capture"); setDeb(null); }} disabled={step === "saving"} data-testid="debrief-redo-btn">
                                    <ArrowCounterClockwise size={14} /> Edit notes & redo
                                </button>
                                <button className="btn btn-primary" onClick={save} disabled={step === "saving"} data-testid="debrief-save-btn">
                                    {step === "saving" ? <CircleNotch size={14} className="animate-spin" /> : <>Save visit{ticked ? ` + ${ticked} item${ticked > 1 ? "s" : ""}` : ""}</>}
                                </button>
                            </div>
                        </>
                    )}

                    {step === "done" && saved && (
                        <div className="text-center py-6 space-y-3" data-testid="debrief-done">
                            <div className="text-lg font-bold">Saved to {client.name}</div>
                            <div className="text-sm text-[var(--rom-text-2)]">
                                Visit logged{saved.tasks ? ` · ${saved.tasks} task${saved.tasks > 1 ? "s" : ""}` : ""}{saved.appointments ? ` · ${saved.appointments} booking${saved.appointments > 1 ? "s" : ""}` : ""}{saved.contacts ? ` · ${saved.contacts} contact${saved.contacts > 1 ? "s" : ""}` : ""}{saved.stage ? ` · moved to ${STAGE_LABELS[saved.stage]}` : ""}
                            </div>
                            <button className="btn btn-primary" onClick={() => onClose(true)} data-testid="debrief-close-btn">Done</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-bold mb-1.5">{title}</div>
            {children}
        </div>
    );
}

function Tick({ on, onToggle, children, testId }) {
    return (
        <button type="button" onClick={onToggle} className={`w-full text-left mt-2 p-2 rounded-[6px] border flex items-start gap-2 ${on ? "border-[var(--rom-navy)] bg-white" : "border-[var(--rom-border)] bg-slate-50 opacity-70"}`} data-testid={testId}>
            {on ? <CheckSquare size={18} weight="fill" className="text-[var(--rom-navy)] shrink-0" /> : <Square size={18} className="text-[var(--rom-text-3)] shrink-0" />}
            <div className="min-w-0">{children}</div>
        </button>
    );
}
